import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  XMarkIcon, 
  LightBulbIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  EyeIcon,
  DocumentTextIcon,
  PhoneIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { 
  createTaskAutomationRule, 
  updateTaskAutomationRule,
  createAutomationRule
} from '../services/automationApi';
import { TaskAutomationRule, DropdownData, TaskAutomationFormData } from '../types/Automation';
import api from '../services/api';

interface DocumentType {
  id: number;
  name: string;
}

interface FormOption {
  id: number;
  name: string;
}

const CALL_TYPES = [
  { value: 'live', label: 'Live Call' },
  { value: 'recorded', label: 'Recorded Call' },
  { value: 'scheduled', label: 'Scheduled Call' }
];

// Available variables for task name
const TASK_NAME_VARIABLES = [
  { key: 'lead_name', label: 'Lead Name', description: 'Full name (First + Last)', example: 'John Smith' },
  { key: 'lead_first_name', label: 'First Name', description: 'First name only', example: 'John' },
  { key: 'lead_last_name', label: 'Last Name', description: 'Last name only', example: 'Smith' },
  { key: 'lead_email', label: 'Email', description: 'Lead email address', example: 'john@example.com' },
  { key: 'lead_phone', label: 'Phone', description: 'Phone number', example: '+1 555-123-4567' },
  { key: 'lead_stage', label: 'Stage', description: 'Current lead stage', example: 'Negotiation' },
  { key: 'lead_company', label: 'Company', description: 'Company name', example: 'Acme Corp' },
  { key: 'date', label: 'Date', description: 'Current date', example: 'Feb 25, 2026' },
  { key: 'date_short', label: 'Short Date', description: 'Short date format', example: '02/25/2026' }
];

// Helper to check if task name has variables
const hasVariables = (name: string): boolean => /\{[a-z_]+\}/i.test(name);

// Generate preview with sample data
const generatePreview = (taskName: string, stageName?: string): string => {
  if (!taskName || !hasVariables(taskName)) return taskName;
  
  const sampleData: Record<string, string> = {
    'lead_name': 'Chloe Anderson',
    'lead_first_name': 'Chloe',
    'lead_last_name': 'Anderson',
    'lead_email': 'chloe@example.com',
    'lead_phone': '+1 555-123-4567',
    'lead_stage': stageName || 'Negotiation',
    'lead_company': 'Tech Solutions Inc',
    'date': new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    'date_short': new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  };
  
  let result = taskName;
  for (const [key, value] of Object.entries(sampleData)) {
    const pattern = new RegExp(`\\{${key}\\}`, 'gi');
    result = result.replace(pattern, value);
  }
  
  return result;
};

interface TaskAutomationFormProps {
  rule?: TaskAutomationRule | null;
  dropdownData: DropdownData | null;
  onClose: () => void;
  onSuccess: () => void;
}

const TaskAutomationForm: React.FC<TaskAutomationFormProps> = ({
  rule,
  dropdownData,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showVariableHelper, setShowVariableHelper] = useState(false);
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);
  const taskNameInputRef = useRef<HTMLInputElement>(null);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [forms, setForms] = useState<FormOption[]>([]);
  const [showConditionConfig, setShowConditionConfig] = useState(false);
  const [formData, setFormData] = useState<TaskAutomationFormData>({
    triggerStageId: 0,
    taskName: '',
    taskCategory: '',
    priority: 'medium',
    startDateOffset: 0,
    dueDateOffset: 1,
    reminderOffset: undefined,
    assigneeId: undefined,
    supervisorId: undefined,
    assigneeIds: [],
    supervisorIds: [],
    assignmentMode: 'assign_all',
    rotationInterval: 1,
    taskDescription: '',
    requiresPermission: false,
    isActive: true,
    onTaskCompletionChangeStage: false,
    completionTargetStageId: undefined,
    completionTargetBranchId: undefined,
    completionStageSelectionMode: 'auto',
    completionAlternateStageId: undefined,
    countryId: null,
    completionConditionType: null,
    completionConditionConfig: null
  });

  useEffect(() => {
    if (rule) {
      setFormData({
        triggerStageId: rule.triggerStageId,
        taskName: rule.taskName,
        taskCategory: rule.taskCategory || '',
        priority: rule.priority,
        startDateOffset: rule.startDateOffset,
        dueDateOffset: rule.dueDateOffset,
        reminderOffset: rule.reminderOffset,
        assigneeId: rule.assigneeId,
        supervisorId: rule.supervisorId,
        assigneeIds: rule.assigneeIds || [],
        supervisorIds: rule.supervisorIds || [],
        assignmentMode: rule.assignmentMode || 'assign_all',
        rotationInterval: rule.rotationInterval || 1,
        taskDescription: rule.taskDescription || '',
        requiresPermission: rule.requiresPermission || false,
        isActive: rule.automationRuleActive || false,
        onTaskCompletionChangeStage: rule.onTaskCompletionChangeStage || false,
        completionTargetStageId: rule.completionTargetStageId,
        completionTargetBranchId: rule.completionTargetBranchId,
        completionStageSelectionMode: rule.completionStageSelectionMode || 'auto',
        completionAlternateStageId: rule.completionAlternateStageId,
        // Preserve any existing country filter on edit. The UI currently hides this
        // field, but we must not silently wipe a filter that was set on the rule.
        countryId: rule.countryId ?? null,
        completionConditionType: rule.completionConditionType || null,
        completionConditionConfig: rule.completionConditionConfig || null
      });
      
      // Show condition config if rule has a completion condition
      if (rule.completionConditionType) {
        setShowConditionConfig(true);
      }
    }
  }, [rule]);

  // Fetch document types and forms for completion conditions
  useEffect(() => {
    const fetchCompletionOptions = async () => {
      try {
        const [docTypesRes, formsRes] = await Promise.all([
          api.get('/documents/types?limit=100'),
          api.get('/forms?limit=100')
        ]);
        
        if (docTypesRes.data?.success && docTypesRes.data?.data) {
          setDocumentTypes(docTypesRes.data.data);
        }
        if (formsRes.data?.success && formsRes.data?.data) {
          setForms(formsRes.data.data.map((f: any) => ({ id: f.id, name: f.name })));
        }
      } catch (err) {
        console.error('Error fetching completion condition options:', err);
      }
    };
    
    fetchCompletionOptions();
  }, []);

  const handleInputChange = (field: keyof TaskAutomationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelectChange = (field: 'assigneeIds' | 'supervisorIds', userId: number, checked: boolean) => {
    setFormData(prev => {
      const currentIds = prev[field] || [];
      if (checked) {
        return {
          ...prev,
          [field]: [...currentIds, userId]
        };
      } else {
        return {
          ...prev,
          [field]: currentIds.filter(id => id !== userId)
        };
      }
    });
  };

  const isUserSelected = (field: 'assigneeIds' | 'supervisorIds', userId: number) => {
    return (formData[field] || []).includes(userId);
  };

  // Get selected stage name for preview
  const selectedStageName = useMemo(() => {
    if (!dropdownData || !formData.triggerStageId) return 'Negotiation';
    const stage = dropdownData.stages.find(s => s.id === formData.triggerStageId);
    return stage?.name || 'Negotiation';
  }, [dropdownData, formData.triggerStageId]);

  // Generate task name preview
  const taskNamePreview = useMemo(() => {
    if (!formData.taskName) return '';
    return generatePreview(formData.taskName, selectedStageName);
  }, [formData.taskName, selectedStageName]);

  // Check if task name has variables
  const taskNameHasVariables = useMemo(() => {
    return hasVariables(formData.taskName || '');
  }, [formData.taskName]);

  // Insert variable at cursor position or append
  const insertVariable = (variableKey: string) => {
    const variable = `{${variableKey}}`;
    const input = taskNameInputRef.current;
    
    if (input) {
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = formData.taskName || '';
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
      
      handleInputChange('taskName', newValue);
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        input.focus();
        const newPosition = start + variable.length;
        input.setSelectionRange(newPosition, newPosition);
      }, 10);
    } else {
      handleInputChange('taskName', (formData.taskName || '') + variable);
    }
  };

  // Copy variable to clipboard
  const copyVariable = async (variableKey: string) => {
    const variable = `{${variableKey}}`;
    try {
      await navigator.clipboard.writeText(variable);
      setCopiedVariable(variableKey);
      setTimeout(() => setCopiedVariable(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.triggerStageId || !formData.taskName || !formData.dueDateOffset) {
      setError('Please fill in all required fields');
      return;
    }

    // Check if at least one assignee is selected
    const hasAssignees = (formData.assigneeIds && formData.assigneeIds.length > 0) || formData.assigneeId;
    if (!hasAssignees) {
      setError('Please select at least one assignee');
      return;
    }

    // For divide mode, check if rotation interval is valid
    if (formData.assignmentMode === 'divide' && (!formData.rotationInterval || formData.rotationInterval < 1)) {
      setError('Please set a valid rotation interval (minimum 1)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (rule) {
        // Update existing rule
        await updateTaskAutomationRule(rule.id, formData);
      } else {
        // Create new rule
        // Get the stage name from dropdown data
        const selectedStage = dropdownData?.stages.find(s => s.id === formData.triggerStageId);
        const stageName = selectedStage?.name || 'Unknown Stage';
        
        // Create unique rule name including stage and timestamp
        const timestamp = new Date().getTime();
        const automationRule = await createAutomationRule({
          name: `Task Automation: ${formData.taskName} [${stageName}] #${timestamp}`,
          description: `Automatically creates task "${formData.taskName}" when lead moves to "${stageName}" stage`,
          ruleType: 'task_automation',
          isActive: formData.isActive ?? true,
          triggerConditions: {
            stageId: formData.triggerStageId
          },
          actionConfig: {
            type: 'create_task',
            taskName: formData.taskName
          }
        });

        // Then create the task automation rule
        await createTaskAutomationRule({
          ...formData,
          automationRuleId: automationRule.id
        });
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save automation rule');
    } finally {
      setLoading(false);
    }
  };

  if (!dropdownData) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading form data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {rule ? 'Edit Task Automation Rule' : 'Create Task Automation Rule'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Trigger Condition */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                When Lead Stage Is <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.triggerStageId}
                onChange={(e) => handleInputChange('triggerStageId', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Select a stage</option>
                {dropdownData.stages.map(stage => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Country Filter - Hidden, always set to "Any Country" (null) */}

            {/* Task Name with Variable Support */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Task Name <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowVariableHelper(!showVariableHelper)}
                  className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  <LightBulbIcon className="h-4 w-4 mr-1" />
                  {showVariableHelper ? 'Hide Variables' : 'Use Variables'}
                  {showVariableHelper ? (
                    <ChevronUpIcon className="h-3 w-3 ml-1" />
                  ) : (
                    <ChevronDownIcon className="h-3 w-3 ml-1" />
                  )}
                </button>
              </div>
              
              <input
                ref={taskNameInputRef}
                type="text"
                value={formData.taskName}
                onChange={(e) => handleInputChange('taskName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Contact {lead_name} - Follow up"
                required
              />

              {/* Variable Helper Panel */}
              {showVariableHelper && (
                <div className="mt-3 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs text-gray-600 mb-3">
                    Click a variable to insert it at your cursor position, or copy it to clipboard.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {TASK_NAME_VARIABLES.map((variable) => (
                      <div
                        key={variable.key}
                        className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:border-indigo-300 hover:shadow-sm transition-all group"
                      >
                        <button
                          type="button"
                          onClick={() => insertVariable(variable.key)}
                          className="flex-1 text-left"
                          title={`${variable.description} (e.g., ${variable.example})`}
                        >
                          <span className="text-xs font-mono text-indigo-600 group-hover:text-indigo-800">
                            {`{${variable.key}}`}
                          </span>
                          <span className="block text-[10px] text-gray-500 truncate">
                            {variable.label}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => copyVariable(variable.key)}
                          className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedVariable === variable.key ? (
                            <CheckIcon className="h-3.5 w-3.5 text-green-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Example Templates */}
                  <div className="mt-3 pt-3 border-t border-indigo-200">
                    <p className="text-xs font-medium text-gray-700 mb-2">Quick Templates:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        'Contact {lead_name}',
                        'Follow up with {lead_first_name}',
                        '{lead_name} - {lead_stage} Follow-up',
                        'Call {lead_name} ({date_short})'
                      ].map((template, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleInputChange('taskName', template)}
                          className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                        >
                          {template}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Preview (only show when variables are used) */}
              {taskNameHasVariables && formData.taskName && (
                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center text-xs text-gray-500 mb-1">
                    <EyeIcon className="h-3.5 w-3.5 mr-1" />
                    Preview (with sample data):
                  </div>
                  <p className="text-sm font-medium text-gray-900">{taskNamePreview}</p>
                </div>
              )}
            </div>

            {/* Task Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Category
              </label>
              <select
                value={formData.taskCategory}
                onChange={(e) => handleInputChange('taskCategory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                {dropdownData.categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {dropdownData.priorities.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder
                </label>
                <select
                  value={formData.reminderOffset || ''}
                  onChange={(e) => handleInputChange('reminderOffset', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No reminder</option>
                  {dropdownData.reminderOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <select
                  value={formData.startDateOffset}
                  onChange={(e) => handleInputChange('startDateOffset', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>Same day as trigger</option>
                  <option value={1}>1 day after trigger</option>
                  <option value={2}>2 days after trigger</option>
                  <option value={3}>3 days after trigger</option>
                  <option value={7}>1 week after trigger</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.dueDateOffset}
                  onChange={(e) => handleInputChange('dueDateOffset', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value={1}>1 day after trigger</option>
                  <option value={2}>2 days after trigger</option>
                  <option value={3}>3 days after trigger</option>
                  <option value={5}>5 days after trigger</option>
                  <option value={7}>1 week after trigger</option>
                  <option value={14}>2 weeks after trigger</option>
                  <option value={30}>1 month after trigger</option>
                </select>
              </div>
            </div>

            {/* Assignment Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignment Mode <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {dropdownData.assignmentModes.map(mode => (
                  <label key={mode.value} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="assignmentMode"
                      value={mode.value}
                      checked={formData.assignmentMode === mode.value}
                      onChange={(e) => handleInputChange('assignmentMode', e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{mode.label}</div>
                      <div className="text-xs text-gray-600">
                        {mode.value === 'assign_all' 
                          ? 'All selected assignees and supervisors will be assigned to every task'
                          : 'Assignees and supervisors will rotate based on the interval you set'
                        }
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Rotation Interval (only show for divide mode) - HIDDEN */}
            {/* {formData.assignmentMode === 'divide' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rotation Interval <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.rotationInterval || 1}
                  onChange={(e) => handleInputChange('rotationInterval', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {dropdownData.rotationIntervals.map(interval => (
                    <option key={interval.value} value={interval.value}>
                      {interval.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-600 mt-1">
                  How many lead stage changes before rotating to the next assignee/supervisor
                </p>
              </div>
            )} */}

            {/* Multiple Assignees Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignees <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                {dropdownData.users.map(user => (
                  <label key={user.id} className="flex items-center space-x-3 py-2 hover:bg-gray-50 rounded px-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isUserSelected('assigneeIds', user.id)}
                      onChange={(e) => handleMultiSelectChange('assigneeIds', user.id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-600">{user.email}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Select one or more users who will be assigned to the task
              </p>
            </div>

            {/* Multiple Supervisors Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supervisors
              </label>
              <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                {dropdownData.users.map(user => (
                  <label key={user.id} className="flex items-center space-x-3 py-2 hover:bg-gray-50 rounded px-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isUserSelected('supervisorIds', user.id)}
                      onChange={(e) => handleMultiSelectChange('supervisorIds', user.id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-600">{user.email}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Select one or more users who will supervise the task (optional)
              </p>
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Description
              </label>
              <textarea
                value={formData.taskDescription}
                onChange={(e) => handleInputChange('taskDescription', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter task description"
              />
            </div>

            {/* Task Completion Condition */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <InformationCircleIcon className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">Completion Requirement (Optional)</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showConditionConfig}
                    onChange={(e) => {
                      setShowConditionConfig(e.target.checked);
                      if (!e.target.checked) {
                        handleInputChange('completionConditionType', null);
                        handleInputChange('completionConditionConfig', null);
                      }
                    }}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">Enable</span>
                </label>
              </div>

              {showConditionConfig && (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500">
                    Auto-created tasks will only be completable when the specified action is performed for the lead.
                  </p>

                  {/* Condition Type Selector */}
                  <div className="grid grid-cols-4 gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('completionConditionType', 'document_upload');
                        handleInputChange('completionConditionConfig', { documentTypeIds: [], requireAll: true });
                      }}
                      className={`px-2 py-1.5 rounded-md border flex items-center justify-center gap-1.5 transition-all text-xs ${
                        formData.completionConditionType === 'document_upload'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <DocumentTextIcon className={`w-4 h-4 ${formData.completionConditionType === 'document_upload' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={formData.completionConditionType === 'document_upload' ? 'text-blue-700 font-medium' : 'text-gray-600'}>
                        Docs
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('completionConditionType', 'call_completed');
                        handleInputChange('completionConditionConfig', { callTypes: [] });
                      }}
                      className={`px-2 py-1.5 rounded-md border flex items-center justify-center gap-1.5 transition-all text-xs ${
                        formData.completionConditionType === 'call_completed'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <PhoneIcon className={`w-4 h-4 ${formData.completionConditionType === 'call_completed' ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={formData.completionConditionType === 'call_completed' ? 'text-green-700 font-medium' : 'text-gray-600'}>
                        Call
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('completionConditionType', 'form_submitted');
                        handleInputChange('completionConditionConfig', { formId: null });
                      }}
                      className={`px-2 py-1.5 rounded-md border flex items-center justify-center gap-1.5 transition-all text-xs ${
                        formData.completionConditionType === 'form_submitted'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <ClipboardDocumentListIcon className={`w-4 h-4 ${formData.completionConditionType === 'form_submitted' ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className={formData.completionConditionType === 'form_submitted' ? 'text-purple-700 font-medium' : 'text-gray-600'}>
                        Form
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('completionConditionType', 'email_sent');
                        handleInputChange('completionConditionConfig', {});
                      }}
                      className={`px-2 py-1.5 rounded-md border flex items-center justify-center gap-1.5 transition-all text-xs ${
                        formData.completionConditionType === 'email_sent'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <EnvelopeIcon className={`w-4 h-4 ${formData.completionConditionType === 'email_sent' ? 'text-orange-600' : 'text-gray-400'}`} />
                      <span className={formData.completionConditionType === 'email_sent' ? 'text-orange-700 font-medium' : 'text-gray-600'}>
                        Email
                      </span>
                    </button>
                  </div>

                  {/* Document Upload Config */}
                  {formData.completionConditionType === 'document_upload' && (
                    <div className="space-y-2 p-2 bg-white border border-blue-200 rounded-lg">
                      <label className="block text-xs font-medium text-gray-700">Required Documents</label>
                      <div className="max-h-32 overflow-y-auto space-y-1 border border-gray-200 rounded-md p-2">
                        {documentTypes.length === 0 ? (
                          <p className="text-xs text-gray-500 italic">No document types found</p>
                        ) : (
                          documentTypes.map((dt) => (
                            <label key={dt.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded text-xs">
                              <input
                                type="checkbox"
                                checked={formData.completionConditionConfig?.documentTypeIds?.includes(dt.id) || false}
                                onChange={(e) => {
                                  const currentIds = formData.completionConditionConfig?.documentTypeIds || [];
                                  const newIds = e.target.checked
                                    ? [...currentIds, dt.id]
                                    : currentIds.filter((id: number) => id !== dt.id);
                                  handleInputChange('completionConditionConfig', {
                                    ...formData.completionConditionConfig,
                                    documentTypeIds: newIds
                                  });
                                }}
                                className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-gray-700">{dt.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Call Completed Config */}
                  {formData.completionConditionType === 'call_completed' && (
                    <div className="space-y-2 p-2 bg-white border border-green-200 rounded-lg">
                      <label className="block text-xs font-medium text-gray-700">Acceptable Call Types</label>
                      <div className="space-y-1">
                        {CALL_TYPES.map((ct) => (
                          <label key={ct.value} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded text-xs">
                            <input
                              type="checkbox"
                              checked={formData.completionConditionConfig?.callTypes?.includes(ct.value) || false}
                              onChange={(e) => {
                                const currentTypes = formData.completionConditionConfig?.callTypes || [];
                                const newTypes = e.target.checked
                                  ? [...currentTypes, ct.value]
                                  : currentTypes.filter((t: string) => t !== ct.value);
                                handleInputChange('completionConditionConfig', {
                                  ...formData.completionConditionConfig,
                                  callTypes: newTypes
                                });
                              }}
                              className="h-3 w-3 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <span className="text-gray-700">{ct.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">Leave all unchecked to accept any call type.</p>
                    </div>
                  )}

                  {/* Form Submitted Config */}
                  {formData.completionConditionType === 'form_submitted' && (
                    <div className="space-y-2 p-2 bg-white border border-purple-200 rounded-lg">
                      <label className="block text-xs font-medium text-gray-700">Required Form</label>
                      <select
                        value={formData.completionConditionConfig?.formId || ''}
                        onChange={(e) => {
                          handleInputChange('completionConditionConfig', {
                            ...formData.completionConditionConfig,
                            formId: e.target.value ? parseInt(e.target.value) : null
                          });
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Any form</option>
                        {forms.map((f) => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Email Sent Config */}
                  {formData.completionConditionType === 'email_sent' && (
                    <div className="space-y-2 p-2 bg-white border border-orange-200 rounded-lg">
                      <p className="text-xs text-gray-600">
                        Task will be marked complete when an email is sent to any lead assigned to this task.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Task Completion Stage Change */}
            <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="onTaskCompletionChangeStage"
                  checked={formData.onTaskCompletionChangeStage || false}
                  onChange={(e) => {
                    handleInputChange('onTaskCompletionChangeStage', e.target.checked);
                    if (!e.target.checked) {
                      handleInputChange('completionStageSelectionMode', 'auto');
                      handleInputChange('completionAlternateStageId', undefined);
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <label htmlFor="onTaskCompletionChangeStage" className="text-sm font-medium text-gray-900">
                    Change lead stage when task is completed
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    When enabled, the lead's stage will change when this auto-created task is marked as completed.
                  </p>
                </div>
              </div>
              
              {formData.onTaskCompletionChangeStage && (
                <div className="ml-7 space-y-4">
                  {/* Selection Mode */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      How should the stage be selected?
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="completionStageSelectionMode"
                          value="auto"
                          checked={formData.completionStageSelectionMode === 'auto'}
                          onChange={() => {
                            handleInputChange('completionStageSelectionMode', 'auto');
                            handleInputChange('completionAlternateStageId', undefined);
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Auto-change to a single stage</span>
                          <p className="text-xs text-gray-500">The lead will automatically move to the selected stage</p>
                        </div>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="completionStageSelectionMode"
                          value="prompt"
                          checked={formData.completionStageSelectionMode === 'prompt'}
                          onChange={() => handleInputChange('completionStageSelectionMode', 'prompt')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Ask user to select between two stages</span>
                          <p className="text-xs text-gray-500">A modal will appear asking the user to choose (e.g., Won or Lost)</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Primary Stage (always shown when enabled) */}
                  <div>
                    <label htmlFor="completionTargetStageId" className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.completionStageSelectionMode === 'prompt' ? 'Primary Stage Option *' : 'Target Stage *'}
                    </label>
                    <select
                      id="completionTargetStageId"
                      value={formData.completionTargetStageId || ''}
                      onChange={(e) => handleInputChange('completionTargetStageId', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      required={formData.onTaskCompletionChangeStage}
                    >
                      <option value="">Select a stage</option>
                      {dropdownData.stages.map(stage => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.completionStageSelectionMode === 'prompt' 
                        ? 'First option in the selection modal (e.g., "Won")'
                        : 'The stage the lead will move to when the task is completed'}
                    </p>
                  </div>

                  {/* Alternate Stage (only for prompt mode) */}
                  {formData.completionStageSelectionMode === 'prompt' && (
                    <div>
                      <label htmlFor="completionAlternateStageId" className="block text-sm font-medium text-gray-700 mb-1">
                        Alternate Stage Option (Or) *
                      </label>
                      <select
                        id="completionAlternateStageId"
                        value={formData.completionAlternateStageId || ''}
                        onChange={(e) => handleInputChange('completionAlternateStageId', e.target.value ? parseInt(e.target.value) : undefined)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required={formData.completionStageSelectionMode === 'prompt'}
                      >
                        <option value="">Select a stage</option>
                        {dropdownData.stages
                          .filter(stage => stage.id !== formData.completionTargetStageId)
                          .map(stage => (
                            <option key={stage.id} value={stage.id}>
                              {stage.name}
                            </option>
                          ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Second option in the selection modal (e.g., "Lost")
                      </p>
                    </div>
                  )}

                  {/* Preview of modal behavior */}
                  {formData.completionStageSelectionMode === 'prompt' && formData.completionTargetStageId && formData.completionAlternateStageId && (
                    <div className="p-3 bg-white border border-blue-200 rounded-md">
                      <p className="text-xs text-gray-600 mb-2">When task is completed, user will see:</p>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                          {dropdownData.stages.find(s => s.id === formData.completionTargetStageId)?.name || 'Stage 1'}
                        </span>
                        <span className="text-gray-500">or</span>
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                          {dropdownData.stages.find(s => s.id === formData.completionAlternateStageId)?.name || 'Stage 2'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Permission Requirement */}
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <input
                type="checkbox"
                id="requiresPermission"
                checked={formData.requiresPermission || false}
                onChange={(e) => handleInputChange('requiresPermission', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div className="flex-1">
                <label htmlFor="requiresPermission" className="text-sm font-medium text-gray-900">
                  Require permission before execution
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  When enabled, a confirmation modal will appear before this automation rule executes, allowing you to approve or reject the action.
                </p>
              </div>
            </div>

            {/* Active/Deactivate Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-900">
                  Rule Status
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  {formData.isActive ? 'This automation rule is currently active and will execute when conditions are met.' : 'This automation rule is inactive and will not execute.'}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span>
                <button
                  type="button"
                  onClick={() => handleInputChange('isActive', !formData.isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    formData.isActive ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (rule ? 'Update Rule' : 'Create Rule')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TaskAutomationForm;
